import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight, Download, Play } from 'lucide-react';
import { BuyerForm } from '@/components/digital/BuyerForm';
import { RefundNotice } from '@/components/digital/RefundNotice';
import { assetUrl, formatPrice } from '@/lib/digitalApi';
import { formatMonthYear } from '@/constants/resourceSections';
import { PHOTOS } from '@/constants/content';

const Attachment = ({ article }) => {
  const [claim, setClaim] = useState(null);
  return (
    <div className="mt-16 bg-white rounded-2xl p-8 md:p-10 border border-taupe/20" data-testid="article-attachment">
      <div className="eyebrow !text-gold mb-3">Companion Download</div>
      <div className="font-serif-display text-3xl tracking-tighter text-matte">
        {article.file_type ? `${article.file_type} \u00b7 ` : ''}{formatPrice(article.price_cents, article.is_free)}
      </div>
      <div className="mt-8">
        {claim ? (
          <Link to={claim.download_path} className="btn-primary" data-testid="article-attachment-download">
            <Download size={14} strokeWidth={1.6} /> Go to Download
          </Link>
        ) : (
          <BuyerForm product={article} onClaimed={setClaim} />
        )}
      </div>
      {!article.is_free && <RefundNotice className="mt-10" />}
    </div>
  );
};

const Ctas = ({ article }) => {
  const { primary_cta: p, secondary_cta: s } = article;
  if (!p && !s) return null;
  const Btn = ({ cta, primary, testid }) => {
    const cls = primary ? 'btn-primary' : 'btn-ghost';
    const inner = (<>{primary && <Play size={12} strokeWidth={1.6} />}{cta.label}{primary ? <ArrowUpRight size={14} strokeWidth={1.6} /> : <ArrowRight size={14} strokeWidth={1.6} />}</>);
    return cta.external
      ? <a href={cta.url} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>{inner}</a>
      : <Link to={cta.url} className={cls} data-testid={testid}>{inner}</Link>;
  };
  return (
    <div className="mt-12 pt-8 border-t border-taupe/25 flex flex-wrap gap-4" data-testid="article-ctas">
      {p && <Btn cta={p} primary testid="article-cta-primary" />}
      {s && <Btn cta={s} testid="article-cta-secondary" />}
    </div>
  );
};

export const ArticleView = ({ article, backTo, backLabel, next }) => (
  <article className="bg-ivory" data-testid="article-page">
    <header className="pt-40 lg:pt-56 pb-16 lg:pb-24">
      <div className="mx-auto max-w-[900px] px-6 sm:px-10">
        <Link to={backTo} className="btn-ghost mb-16" data-testid="article-back">
          <ArrowLeft size={14} strokeWidth={1.6} /> {backLabel}
        </Link>
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <span className="eyebrow text-taupe">{article.kicker || 'From My Desk'}</span>
          <span className="w-1 h-1 rounded-full bg-gold" aria-hidden />
          <span className="font-label text-[11px] tracking-widest uppercase text-matte/60" data-testid="article-date">{formatMonthYear(article.published_date)}</span>
          {article.read_time && (<><span className="w-1 h-1 rounded-full bg-gold" aria-hidden /><span className="font-label text-[11px] tracking-widest uppercase text-matte/60">{article.read_time}</span></>)}
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tightest text-matte break-words" data-testid="article-title">
          {article.title}
        </h1>
        {article.short_description && (
          <p className="mt-10 font-serif-display italic text-2xl md:text-3xl leading-snug text-taupe max-w-3xl">{article.short_description}</p>
        )}
      </div>
    </header>

    <section className="pb-16">
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10">
        <div className="rounded-3xl overflow-hidden luxury-shadow">
          <img src={article.cover_url ? assetUrl(article.cover_url) : PHOTOS.editorial} alt={article.title} className={`w-full h-[50vh] lg:h-[68vh] object-cover ${article.cover_url ? '' : 'object-[center_22%]'}`} loading="lazy" data-testid="article-featured-image" />
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-[720px] px-6 sm:px-10 text-matte leading-[1.85] text-[17px]">
        <div className="article-body" data-testid="article-body" dangerouslySetInnerHTML={{ __html: article.body_html }} />
        <div className="pt-12">
          <span className="hairline-gold h-px w-16 inline-block" aria-hidden />
          <div className="eyebrow text-taupe mt-6">Toia Wallace · Founder</div>
        </div>
        <Ctas article={article} />
        {article.has_file && <Attachment article={article} />}
      </div>
    </section>

    {next && (
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10">
          <div className="eyebrow text-taupe mb-6">Next Entry</div>
          <Link to={next.href} className="group block max-w-3xl" data-testid="article-next">
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte group-hover:text-gold transition-colors duration-500">{next.title}</h2>
            <p className="mt-6 text-matte/70 leading-relaxed max-w-2xl">{next.short_description}</p>
            <div className="mt-10 inline-flex items-center gap-3 text-matte group-hover:text-gold transition-colors">
              <span className="font-label text-[11px] tracking-widest uppercase">Continue Reading</span>
              <ArrowRight size={16} strokeWidth={1.4} />
            </div>
          </Link>
        </div>
      </section>
    )}
  </article>
);
